import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  CalendarDays,
  Download,
  FileDown,
  FileSpreadsheet,
  FileText,
  RefreshCw,
  AlertCircle,
  Info,
} from 'lucide-react';
import { useCpPortal } from '../../hooks/useCpPortal';

// ─── Types (mirror backend response) ─────────────────────────────────────────

interface ReportRow {
  period: string;
  groupId: 'payments' | 'vas';
  groupLabel: string;
  categoryId: string;
  label: string;
  txCount: number;
  totalCents: number;
}

interface ReportTotals {
  txCount: number;
  totalCents: number;
  byGroup: Record<string, { label: string; txCount: number; totalCents: number }>;
}

interface ReportData {
  rows: ReportRow[];
  totals: ReportTotals;
  attributedUsers: number;
  period: { from: string; to: string; groupBy: GroupBy };
}

type GroupBy = 'day' | 'week' | 'month';
type Preset = '7d' | '30d' | '90d' | 'month' | 'custom';
type ExportFormat = 'csv' | 'xlsx' | 'pdf';

const MAX_RANGE_DAYS = 366;

const GROUP_FILTERS: { id: 'payments' | 'vas'; label: string }[] = [
  { id: 'payments', label: 'Payments' },
  { id: 'vas', label: 'VAS' },
];

// ─── Date helpers (SAST date strings) ────────────────────────────────────────

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function presetRange(preset: Exclude<Preset, 'custom'>): { from: string; to: string } {
  const today = new Date();
  const to = toIsoDate(today);
  if (preset === 'month') {
    return { from: toIsoDate(new Date(today.getFullYear(), today.getMonth(), 1)), to };
  }
  const days = preset === '7d' ? 6 : preset === '30d' ? 29 : 89;
  return { from: toIsoDate(new Date(today.getTime() - days * 86400000)), to };
}

function defaultGroupBy(from: string, to: string): GroupBy {
  const days = Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86400000) + 1;
  if (days > 92) return 'month';
  if (days > 35) return 'week';
  return 'day';
}

function rangeDays(from: string, to: string): number {
  return Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86400000) + 1;
}

function formatPeriodLabel(period: string, groupBy: GroupBy): string {
  const d = new Date(`${period}T00:00:00+02:00`);
  if (groupBy === 'month') {
    return d.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric', timeZone: 'Africa/Johannesburg' });
  }
  if (groupBy === 'week') {
    return `Week of ${d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Africa/Johannesburg' })}`;
  }
  return d.toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Africa/Johannesburg' });
}

const formatRand = (cents: number) =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(cents / 100);

const formatCount = (n: number) => n.toLocaleString('en-ZA');

// ─── Component ───────────────────────────────────────────────────────────────

export const ClientTransactionReportsOverlay: React.FC = () => {
  const { apiBase, authHeaders, user } = useCpPortal();

  const [preset, setPreset] = useState<Preset>('30d');
  const [from, setFrom] = useState(() => presetRange('30d').from);
  const [to, setTo] = useState(() => presetRange('30d').to);
  const [groupBy, setGroupBy] = useState<GroupBy>(() => defaultGroupBy(presetRange('30d').from, presetRange('30d').to));
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());

  const [report, setReport] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportingFormat, setExportingFormat] = useState<ExportFormat | null>(null);

  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (p === 'custom') return;
    const range = presetRange(p);
    setFrom(range.from);
    setTo(range.to);
    setGroupBy(defaultGroupBy(range.from, range.to));
  };

  const buildQuery = useCallback((): string => {
    const params = new URLSearchParams();
    params.set('from', from);
    params.set('to', to);
    params.set('groupBy', groupBy);
    if (selectedGroups.size > 0) params.set('groups', [...selectedGroups].join(','));
    if (selectedCategories.size > 0) params.set('categories', [...selectedCategories].join(','));
    return params.toString();
  }, [from, to, groupBy, selectedGroups, selectedCategories]);

  const fetchReport = useCallback(async () => {
    if (!from || !to) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/reports/transaction-mix?${buildQuery()}`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        setReport(data.data as ReportData);
      } else {
        setError(data.error || 'Failed to load report');
        setReport(null);
      }
    } catch {
      setError('Network error loading report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [apiBase, authHeaders, buildQuery, from, to]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleExport = async (format: ExportFormat) => {
    setExportingFormat(format);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/reports/transaction-mix/export?${buildQuery()}&format=${format}`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || 'Export failed');
      }
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match?.[1] || `transaction-mix.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExportingFormat(null);
    }
  };

  const toggleSet = (set: Set<string>, value: string): Set<string> => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
  };

  const availableCategories = useMemo(() => {
    if (!report) return [];
    const byId = new Map<string, { categoryId: string; label: string; groupId: string }>();
    for (const row of report.rows) {
      if (!byId.has(row.categoryId)) {
        byId.set(row.categoryId, { categoryId: row.categoryId, label: row.label, groupId: row.groupId });
      }
    }
    return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [report]);

  const rowsByPeriod = useMemo(() => {
    if (!report) return [];
    const map = new Map<string, ReportRow[]>();
    for (const row of report.rows) {
      const list = map.get(row.period) || [];
      list.push(row);
      map.set(row.period, list);
    }
    return [...map.entries()].map(([period, rows]) => ({
      period,
      rows,
      txCount: rows.reduce((s, r) => s + r.txCount, 0),
      totalCents: rows.reduce((s, r) => s + r.totalCents, 0),
    }));
  }, [report]);

  const customRangeInvalid = preset === 'custom' && (!from || !to || from > to || rangeDays(from, to) > MAX_RANGE_DAYS);

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-2.5">
          <BarChart3 className="mt-0.5 h-5 w-5 text-[var(--primary)]" aria-hidden />
          <div>
            <h2 className="text-sm font-semibold text-[var(--foreground)]">Transaction mix</h2>
            <p className="mt-0.5 text-xs text-[var(--muted-foreground)]">
              Aggregated activity of your registered users — counts and totals by category.
              No individual customer data.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={fetchReport}
            disabled={loading}
            className="flex min-h-[36px] items-center gap-1.5 rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-1.5 text-sm text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          {(['csv', 'xlsx', 'pdf'] as ExportFormat[]).map((format) => {
            const Icon = format === 'csv' ? FileText : format === 'xlsx' ? FileSpreadsheet : FileDown;
            return (
              <button
                key={format}
                type="button"
                onClick={() => handleExport(format)}
                disabled={loading || exportingFormat !== null || !report || report.rows.length === 0}
                className="flex min-h-[36px] items-center gap-1.5 rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-1.5 text-sm text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] disabled:opacity-50"
              >
                {exportingFormat === format ? (
                  <RefreshCw className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Icon className="h-4 w-4" aria-hidden />
                )}
                <span className="uppercase">{format}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Controls */}
      <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-3">
            <div>
              <p className="mb-1.5 text-xs font-medium text-[var(--foreground)]">Date range</p>
              <div className="flex flex-wrap gap-1.5">
                {([
                  ['7d', 'Last 7 days'],
                  ['30d', 'Last 30 days'],
                  ['90d', 'Last 90 days'],
                  ['month', 'This month'],
                  ['custom', 'Custom'],
                ] as [Preset, string][]).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => applyPreset(value)}
                    className={`min-h-[36px] rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                      preset === value
                        ? 'border-[var(--primary)] bg-[var(--primary)]/10 font-medium text-[var(--primary)]'
                        : 'border-[var(--border)] text-[var(--muted-foreground)] hover:bg-[var(--muted)]'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {preset === 'custom' && (
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label htmlFor="report-from" className="mb-1 block text-xs font-medium text-[var(--foreground)]">From</label>
                  <input
                    id="report-from"
                    type="date"
                    value={from}
                    max={to}
                    onChange={(e) => setFrom(e.target.value)}
                    className="min-h-[36px] rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-1.5 text-sm text-[var(--foreground)] outline-none focus:border-[var(--primary)] focus:ring-1 focus:ring-[var(--primary)]"
                  />
                </div>
                <div>
                  <label htmlFor="report-to" className="mb-1 block text-xs font-medium text-[var(--foreground)]">To</label>
                  <input
                    id="report-to"
                    type="date"
                    value={to}
                    min={from}
                    max={toIsoDate(new Date())}
                    onChange={(e) => setTo(e.target.value)}
                    className="min-h-[36px] rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-1.5 text-sm text-[var(--foreground)] outline-none focus:border-[var(--primary)] focus:ring-1 focus:ring-[var(--primary)]"
                  />
                </div>
                {customRangeInvalid && (
                  <p className="text-xs text-red-600">
                    Enter a valid range of up to {MAX_RANGE_DAYS} days.
                  </p>
                )}
              </div>
            )}
          </div>

          <div>
            <p className="mb-1.5 text-xs font-medium text-[var(--foreground)]">Group by</p>
            <div className="flex overflow-hidden rounded-lg border border-[var(--border)]">
              {(['day', 'week', 'month'] as GroupBy[]).map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setGroupBy(value)}
                  className={`min-h-[36px] px-3.5 py-1.5 text-sm capitalize transition-colors ${
                    groupBy === value
                      ? 'bg-[var(--primary)] font-medium text-[var(--primary-foreground)]'
                      : 'bg-[var(--card)] text-[var(--muted-foreground)] hover:bg-[var(--muted)]'
                  }`}
                >
                  {value === 'day' ? 'Daily' : value === 'week' ? 'Weekly' : 'Monthly'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Filter chips */}
        <div className="mt-4 space-y-2 border-t border-[var(--border)] pt-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-xs font-medium text-[var(--muted-foreground)]">Groups:</span>
            {GROUP_FILTERS.map((g) => {
              const active = selectedGroups.has(g.id);
              return (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setSelectedGroups((s) => toggleSet(s, g.id))}
                  aria-pressed={active}
                  className={`min-h-[32px] rounded-full border px-3 py-1 text-xs transition-colors ${
                    active
                      ? 'border-[var(--primary)] bg-[var(--primary)] font-medium text-[var(--primary-foreground)]'
                      : 'border-[var(--border)] text-[var(--muted-foreground)] hover:bg-[var(--muted)]'
                  }`}
                >
                  {g.label}
                </button>
              );
            })}
          </div>
          {availableCategories.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="mr-1 text-xs font-medium text-[var(--muted-foreground)]">Categories:</span>
              {availableCategories.map((c) => {
                const active = selectedCategories.has(c.categoryId);
                return (
                  <button
                    key={c.categoryId}
                    type="button"
                    onClick={() => setSelectedCategories((s) => toggleSet(s, c.categoryId))}
                    aria-pressed={active}
                    className={`min-h-[32px] rounded-full border px-3 py-1 text-xs transition-colors ${
                      active
                        ? 'border-[var(--primary)] bg-[var(--primary)] font-medium text-[var(--primary-foreground)]'
                        : 'border-[var(--border)] text-[var(--muted-foreground)] hover:bg-[var(--muted)]'
                    }`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          )}
          {(selectedGroups.size > 0 || selectedCategories.size > 0) && (
            <button
              type="button"
              onClick={() => { setSelectedGroups(new Set()); setSelectedCategories(new Set()); }}
              className="text-xs font-medium text-[var(--primary)] hover:underline"
            >
              Clear all filters
            </button>
          )}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700" role="alert">
          <AlertCircle className="h-4 w-4 flex-shrink-0" aria-hidden />
          <span>{error}</span>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard
          label="Transactions"
          value={report ? formatCount(report.totals.txCount) : '—'}
          loading={loading}
        />
        <KpiCard
          label="Payments"
          value={report ? formatRand(report.totals.byGroup.payments?.totalCents ?? 0) : '—'}
          hint={report ? `${formatCount(report.totals.byGroup.payments?.txCount ?? 0)} transactions` : undefined}
          loading={loading}
        />
        <KpiCard
          label="VAS"
          value={report ? formatRand(report.totals.byGroup.vas?.totalCents ?? 0) : '—'}
          hint={report ? `${formatCount(report.totals.byGroup.vas?.txCount ?? 0)} transactions` : undefined}
          loading={loading}
        />
        <KpiCard
          label="Total value"
          value={report ? formatRand(report.totals.totalCents) : '—'}
          hint={report ? `${formatCount(report.attributedUsers)} registered users` : undefined}
          loading={loading}
        />
      </div>

      {/* Data table */}
      <div className="overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--card)]">
        {loading ? (
          <div className="space-y-2 p-4" aria-busy="true">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 animate-pulse rounded-lg bg-[var(--muted)]/60" />
            ))}
          </div>
        ) : !report || rowsByPeriod.length === 0 ? (
          <div className="p-10 text-center">
            <CalendarDays className="mx-auto mb-3 h-8 w-8 text-[var(--muted-foreground)]" aria-hidden />
            <p className="text-sm font-medium text-[var(--foreground)]">No transactions in this period</p>
            <p className="mt-1 text-xs text-[var(--muted-foreground)]">
              Try a wider date range or clear the category filters.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] bg-[var(--muted)]/40 text-left">
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[var(--muted-foreground)]">Group</th>
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[var(--muted-foreground)]">Category</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-[var(--muted-foreground)]">Transactions</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-[var(--muted-foreground)]">Total (ZAR)</th>
                </tr>
              </thead>
              <tbody>
                {rowsByPeriod.map((section) => (
                  <React.Fragment key={section.period}>
                    <tr className="bg-[var(--primary)]/5">
                      <td colSpan={4} className="px-4 py-2 text-xs font-semibold text-[var(--foreground)]">
                        {formatPeriodLabel(section.period, groupBy)}
                      </td>
                    </tr>
                    {section.rows.map((row) => (
                      <tr key={`${section.period}-${row.categoryId}`} className="border-b border-[var(--border)] last:border-0 hover:bg-[var(--muted)]/30">
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                            row.groupId === 'vas'
                              ? 'border-violet-200 bg-violet-50 text-violet-700'
                              : 'border-sky-200 bg-sky-50 text-sky-700'
                          }`}>
                            {row.groupId === 'vas' ? 'VAS' : 'Payments'}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 font-medium text-[var(--foreground)]">{row.label}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-[var(--muted-foreground)]">{formatCount(row.txCount)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums font-medium text-[var(--foreground)]">{formatRand(row.totalCents)}</td>
                      </tr>
                    ))}
                    <tr className="border-b border-[var(--border)] bg-[var(--muted)]/30">
                      <td colSpan={2} className="px-4 py-2 text-xs font-medium text-[var(--muted-foreground)]">
                        {formatPeriodLabel(section.period, groupBy)} total
                      </td>
                      <td className="px-4 py-2 text-right text-xs font-semibold tabular-nums text-[var(--foreground)]">{formatCount(section.txCount)}</td>
                      <td className="px-4 py-2 text-right text-xs font-semibold tabular-nums text-[var(--foreground)]">{formatRand(section.totalCents)}</td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[var(--primary)]/30 bg-[var(--card)]">
                  <td colSpan={2} className="px-4 py-3 text-sm font-semibold text-[var(--foreground)]">
                    Grand total · {formatPeriodRange(report.period.from, report.period.to)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-bold tabular-nums text-[var(--foreground)]">
                    {formatCount(report.totals.txCount)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-bold tabular-nums text-[var(--foreground)]">
                    {formatRand(report.totals.totalCents)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Data boundary note */}
      <div className="flex items-start gap-2 rounded-xl border border-[var(--border)] bg-[var(--muted)]/30 px-4 py-3">
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--muted-foreground)]" aria-hidden />
        <p className="text-xs text-[var(--muted-foreground)]">
          This report shows aggregated, anonymised activity of customers who registered through
          {user?.companyName ? ` ${user.companyName}'s` : ' your'} partner link. Figures include completed
          transactions only and are grouped automatically as new payment and VAS products launch.
        </p>
      </div>
    </div>
  );
};

function formatPeriodRange(from: string, to: string): string {
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Africa/Johannesburg' };
  const f = new Date(`${from}T00:00:00+02:00`).toLocaleDateString('en-ZA', opts);
  const t = new Date(`${to}T00:00:00+02:00`).toLocaleDateString('en-ZA', opts);
  return `${f} – ${t}`;
}

const KpiCard: React.FC<{ label: string; value: string; hint?: string; loading?: boolean }> = ({
  label,
  value,
  hint,
  loading,
}) => (
  <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
    <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--muted-foreground)]">{label}</p>
    {loading ? (
      <div className="mt-2 h-6 w-24 animate-pulse rounded bg-[var(--muted)]/60" />
    ) : (
      <p className="mt-1 truncate text-lg font-bold tabular-nums text-[var(--foreground)]">{value}</p>
    )}
    {hint && !loading && <p className="mt-0.5 text-[11px] text-[var(--muted-foreground)]">{hint}</p>}
  </div>
);
